import type { JSX } from 'react';
import type { DropzoneOptions } from 'react-dropzone';
import { Alert, Button, Icon, Text } from '@ksyos/design-system';
import ReactDropzone from 'react-dropzone';
import { styled } from 'styled-components';
import { attachmentLimit } from '@common/validation/asset';
import { DropzoneFile } from './DropzoneFile';

export type FileBase = {
    filename: string;
    isUploading: boolean;
    key: string;
    mimeType: string;
    thumbnailUrl?: string;
    uploadProgressPercentage?: number;
};

type DropzoneProps<File extends FileBase> = Readonly<{
    acceptedMimeTypes: DropzoneOptions['accept'];
    errorTexts?: string[];
    files: File[];
    maxFiles?: DropzoneOptions['maxFiles'];
    maxSize?: DropzoneOptions['maxSize'];
    onFileRemove: (file: File) => Promise<void> | void;
    onFilesAdded: DropzoneOptions['onDropAccepted'];
    onFilesRejected: DropzoneOptions['onDropRejected'];
    onThumbnailClick: (file: File) => Promise<void> | void;
}>;

const defaultMaxFiles = attachmentLimit;
const defaultMaxSize = 1073741824; // 1 Gigabyte (1024 * 1024 * 1024)

/*
 * This component should ideally be part of the Design System.
 * It should also use the Design System tokens for colors, radius, and spacing (e.g. `24px`).
 */
export function Dropzone<File extends FileBase>(props: DropzoneProps<File>): JSX.Element {
    const {
        acceptedMimeTypes,
        errorTexts,
        files,
        maxFiles,
        maxSize,
        onFileRemove,
        onFilesAdded,
        onFilesRejected,
        onThumbnailClick,
    } = props;

    return (
        <>
            <ReactDropzone
                accept={acceptedMimeTypes}
                maxFiles={maxFiles ?? defaultMaxFiles}
                maxSize={maxSize ?? defaultMaxSize}
                onDropAccepted={onFilesAdded}
                onDropRejected={onFilesRejected}
            >
                {({ getRootProps, getInputProps }) => (
                    <StyledDropzone
                        $hasErrors={errorTexts ? errorTexts.length > 0 : false}
                        data-automation-test="dropzone"
                        {...getRootProps()}
                    >
                        {files.map((file) => (
                            <DropzoneFile
                                filename={file.filename}
                                key={file.key}
                                mimeType={file.mimeType}
                                onFileRemove={() => onFileRemove(file)}
                                onThumbnailClick={() => onThumbnailClick(file)}
                                thumbnailUrl={file.thumbnailUrl}
                                uploadProgressPercentage={file.uploadProgressPercentage}
                            />
                        ))}
                        <input {...getInputProps()} />
                        <Placeholder $hasFiles={files.length > 0}>
                            <Icon
                                color="primary"
                                icon="upload"
                                size="36"
                                sx={{
                                    border: '1px solid #dbd5d0',
                                    borderRadius: '50%',
                                    padding: '8px',
                                }}
                            />
                            <div>
                                <Text fontWeight="bold">
                                    Sleep je {props.maxFiles && props.maxFiles < 2 ? 'bestand' : 'bestanden'} in dit vak
                                </Text>
                                <div style={{ alignItems: 'center', display: 'flex', gap: '4px' }}>
                                    <span style={{ color: 'black', fontWeight: 'normal', textDecoration: 'none' }}>
                                        of
                                    </span>
                                    <Button
                                        size="small"
                                        tabIndex={-1}
                                        variant="link"
                                    >
                                        zoek een bestand
                                    </Button>
                                </div>
                            </div>
                        </Placeholder>
                    </StyledDropzone>
                )}
            </ReactDropzone>
            {errorTexts?.map((errorText) => (
                <Alert
                    appearance="warning"
                    key={errorText}
                >
                    {errorText}
                </Alert>
            ))}
        </>
    );
}

const StyledDropzone = styled.div<{ $hasErrors: boolean }>`
    border: ${({ $hasErrors }) =>
        $hasErrors ? '1px dashed var(--kds-color-border-warning)' : '1px dashed var(--kds-color-border-medium)'};
    border-radius: var(--kds-border-radius-base);
    padding: var(--kds-spacing-6);

    &:hover {
        cursor: pointer;

        button {
            text-decoration: underline;
        }
    }
`;

const Placeholder = styled.div<{ $hasFiles: boolean }>`
    align-items: center;
    clear: both;
    display: flex;
    gap: var(--kds-spacing-6);
    padding-top: ${({ $hasFiles }) => ($hasFiles ? 'var(--kds-spacing-6)' : '0')};
    width: 100%;

    & > div {
        align-items: flex-start;
        display: flex;
        flex-direction: column;
    }
`;
